import {Injectable} from '@angular/core';
import {APIService} from './api.service';
import {Organization} from '../interfaces/client';
import {OrganizationVacancy, PageMeta} from '../interfaces/organization-vacancy';

export interface OrganizationSearchResult {
  id: number;
  name: string;
  aliases_count?: number;
  linked_client_status?: number | null;
  has_existing_client_id?: number | null;
}

@Injectable({providedIn: 'root'})
export class OrganizationService extends APIService {

  /**
   * Typeahead search backed by /api/search/organization. Backend tries ES,
   * falls back to SQL LIKE so the typeahead never goes fully dark.
   */
  public async search(query: string, page = 1): Promise<OrganizationSearchResult[]> {
    const res = await this.post<any, any>('/search/organization', {
      filter: {search: query},
      sort: null,
      page,
    });
    return (res?.results ?? []) as OrganizationSearchResult[];
  }

  public async fetch(id: number): Promise<Organization> {
    const res = await this.get<{data: Organization}>('/organizations/' + id, 'Could not load organization #' + id);
    return res.data;
  }

  /**
   * Paginated directory list. `sort` takes canonical_name or created_at only:
   * `id` is rejected by the backend because it has no Elasticsearch mapping and
   * could therefore only ever sort on the SQL fallback.
   */
  public async list(params: OrganizationListParams = {}): Promise<{data: Organization[]; meta: PageMeta} | undefined> {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');
    return this.get<{data: Organization[]; meta: PageMeta}>(
      '/organizations' + (qs ? '?' + qs : ''),
      'Could not load the organizations directory',
    );
  }

  /**
   * Vacancies for one org. Role-gated by MarketVacancyPolicy on the backend,
   * which is BROADER than the directory page's own gate, so a failure here is a
   * real error rather than a page-level role mismatch.
   */
  public async vacancies(id: number, page = 1, perPage = 25): Promise<{data: OrganizationVacancy[]; meta: PageMeta} | undefined> {
    return this.get<{data: OrganizationVacancy[]; meta: PageMeta}>(
      `/organizations/${id}/vacancies?page=${page}&per_page=${perPage}`,
      'Could not load vacancies for this organization',
    );
  }
}

export interface OrganizationListParams {
  q?: string;
  page?: number;
  per_page?: number;
  sort?: 'canonical_name' | 'created_at';
  direction?: 'asc' | 'desc';
}
